import React, { useState, useEffect, useRef } from 'react';
import './CreateTemplateModal.css';

/**
 * CreateTemplateModal Component
 * 
 * Modal for creating a new template.
 * Simple form with just name and description.
 */
const CreateTemplateModal = ({ 
  isOpen, 
  onClose, 
  onTemplateCreated,
}) => {
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const nameInputRef = useRef(null);

  // Focus name input when modal opens
  useEffect(() => {
    if (isOpen && nameInputRef.current) {
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTemplateName('');
      setDescription('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!templateName.trim()) {
      setError('Please enter a template name');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newTemplate = {
        name: templateName.trim(),
        description: description.trim() || undefined,
        isTemplate: true,
        status: 'active',
        tasks: [],
        categories: [],
      };
      
      onTemplateCreated?.(newTemplate);
    } catch (err) {
      setError('Failed to create template. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTemplateName('');
    setDescription('');
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal modal--template" onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <div className="modal__header-icon">📋</div>
          <h2 className="modal__title">Create New Template</h2>
          <button className="modal__close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal__body">
            {error && (
              <div className="modal__error">{error}</div>
            )}

            <div className="modal__field">
              <label className="modal__label">Template Name</label>
              <input
                ref={nameInputRef}
                type="text"
                className="modal__input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Product Launch Workflow"
                disabled={loading}
              />
            </div>

            <div className="modal__field">
              <label className="modal__label">
                Description 
                <span className="modal__label-optional">(optional)</span>
              </label>
              <textarea
                className="modal__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what this template is for..."
                disabled={loading}
                rows={3}
              />
            </div>

            <div className="modal__info">
              <div className="modal__info-icon">💡</div>
              <p className="modal__info-text">
                Templates are reusable blueprints. After creating, you can add missions 
                and use this template to quickly create new campaigns.
              </p>
            </div>
          </div>

          <div className="modal__footer">
            <button 
              type="button" 
              className="modal__btn modal__btn--secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="modal__btn modal__btn--primary modal__btn--template"
              disabled={loading || !templateName.trim()}
            >
              {loading ? 'Creating...' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTemplateModal;

